import { toValidMapAddress } from '../utils/validators/map-address.validator';

export interface MapCoordinates {
  lat: number;
  lng: number;
}

export interface NormalizedMapUrl {
  embedUrl: string;
  canonicalUrl: string;
  address?: string;
  query?: string;
  coordinates?: MapCoordinates;
}

const GOOGLE_MAPS_EMBED_BASE = 'https://www.google.com/maps';
const GOOGLE_MAPS_EMBED_OUTPUT = `${GOOGLE_MAPS_EMBED_BASE}`;

const GOOGLE_SHORT_HOSTS = new Set([
  'maps.app.goo.gl',
  'goo.gl',
  'goo.gl/maps',
]);

function ensureProtocol(url: string): string {
  if (!url) {
    return url;
  }
  if (/^https?:\/\//i.test(url)) {
    return url;
  }
  return `https://${url}`;
}

function decodeMapComponent(value: string | null): string | undefined {
  if (!value) {
    return undefined;
  }
  try {
    return decodeURIComponent(value).replace(/\+/g, ' ').trim() || undefined;
  } catch {
    return value.replace(/\+/g, ' ').trim() || undefined;
  }
}

function parseLatLng(value: string | null): MapCoordinates | undefined {
  if (!value) {
    return undefined;
  }
  const match = value.match(/(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/);
  if (!match) {
    return undefined;
  }
  const lat = Number(match[1]);
  const lng = Number(match[2]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return undefined;
  }
  return { lat, lng };
}

function parseCoordsFromPath(pathname: string): MapCoordinates | undefined {
  const match = pathname.match(/@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/);
  if (match) {
    return parseLatLng(`${match[1]},${match[2]}`);
  }
  return undefined;
}

function parseAddressFromPath(pathname: string): string | undefined {
  const segments = pathname.split('/').filter(Boolean);
  const placeIndex = segments.findIndex(segment => segment.toLowerCase() === 'place');
  if (placeIndex !== -1 && segments[placeIndex + 1]) {
    return decodeMapComponent(segments[placeIndex + 1]);
  }
  const dirIndex = segments.findIndex(segment => segment.toLowerCase() === 'dir');
  if (dirIndex !== -1) {
    const destination = segments.slice(dirIndex + 1).find(segment => segment.length > 0);
    if (destination) {
      return decodeMapComponent(destination);
    }
  }
  return undefined;
}

function isGoogleMapsHost(hostname: string): boolean {
  const normalizedHost = hostname.toLowerCase();
  if (GOOGLE_SHORT_HOSTS.has(normalizedHost)) {
    return true;
  }
  if (normalizedHost === 'google.com' || normalizedHost === 'www.google.com') {
    return true;
  }
  if (/^(?:maps\.)?google\.[a-z.]+$/.test(normalizedHost)) {
    return true;
  }
  if (/^[a-z.]*\.google\.[a-z.]+$/.test(normalizedHost) && normalizedHost.includes('.maps.')) {
    return true;
  }
  return false;
}

function isMapsPath(url: URL): boolean {
  const path = url.pathname.toLowerCase();
  return path.startsWith('/maps');
}

function buildEmbedFromQuery(query: string): string {
  return `${GOOGLE_MAPS_EMBED_OUTPUT}?q=${encodeURIComponent(query)}&output=embed`;
}

function buildCanonicalFromQuery(query: string): string {
  return `${GOOGLE_MAPS_EMBED_BASE}/search/?api=1&query=${encodeURIComponent(query)}`;
}

function normalizeFromGoogleUrl(url: URL): NormalizedMapUrl | null {
  if (!isMapsPath(url) && !url.searchParams.has('q') && !url.searchParams.has('query')) {
    return null;
  }

  const params = url.searchParams;
  const directQuery = decodeMapComponent(params.get('query'))
    || decodeMapComponent(params.get('q'))
    || decodeMapComponent(params.get('daddr'))
    || decodeMapComponent(params.get('destination'))
    || decodeMapComponent(params.get('saddr'));

  const coords = parseLatLng(params.get('ll'))
    || parseLatLng(params.get('center'))
    || parseLatLng(params.get('destination_coordinates'))
    || parseCoordsFromPath(url.pathname)
    || (directQuery ? parseLatLng(directQuery) : undefined);

  const address = directQuery || parseAddressFromPath(url.pathname);
  const normalizedAddress = address ? toValidMapAddress(address) : undefined;

  const query = coords
    ? `${coords.lat},${coords.lng}`
    : (normalizedAddress || address || undefined);

  if (!query) {
    return null;
  }

  const canonical = buildCanonicalFromQuery(query);
  const embedUrl = buildEmbedFromQuery(query);

  return {
    embedUrl,
    canonicalUrl: canonical,
    address: normalizedAddress || address,
    query,
    coordinates: coords,
  };
}

function normalizeFromShortUrl(url: URL, depth: number): NormalizedMapUrl | null {
  if (depth > 3) {
    return null;
  }
  const params = url.searchParams;
  const linkParam = params.get('link') || params.get('url');
  if (linkParam) {
    return normalizeGoogleMapUrl(linkParam, depth + 1);
  }
  const qParam = params.get('q');
  if (qParam) {
    const query = decodeMapComponent(qParam);
    if (query) {
      return {
        embedUrl: buildEmbedFromQuery(query),
        canonicalUrl: buildCanonicalFromQuery(query),
        address: toValidMapAddress(query) || query,
        query,
      };
    }
  }
  return null;
}

export function normalizeGoogleMapUrl(rawUrl?: string | null, depth: number = 0): NormalizedMapUrl | null {
  if (!rawUrl) {
    return null;
  }

  const trimmed = rawUrl.trim();
  if (!trimmed) {
    return null;
  }

  const withProtocol = ensureProtocol(trimmed);

  let parsed: URL;
  try {
    parsed = new URL(withProtocol);
  } catch {
    return null;
  }

  const hostname = parsed.hostname.toLowerCase();

  if (!isGoogleMapsHost(hostname)) {
    return null;
  }

  if (GOOGLE_SHORT_HOSTS.has(hostname)) {
    const normalized = normalizeFromShortUrl(parsed, depth);
    if (normalized) {
      return normalized;
    }
    // If we can't resolve the short link, treat as unsupported.
    return null;
  }

  // Handle goo.gl/maps where hostname might be "goo.gl" and pathname starts with /maps
  if (hostname === 'goo.gl' && parsed.pathname.startsWith('/maps')) {
    const normalized = normalizeFromShortUrl(parsed, depth);
    if (normalized) {
      return normalized;
    }
    return null;
  }

  return normalizeFromGoogleUrl(parsed);
}

export function normalizeMapUrl(rawUrl?: string | null): NormalizedMapUrl | null {
  return normalizeGoogleMapUrl(rawUrl);
}
